/**
 * The Instagram step's decision rules (VIBE-1990) — the twin of `./metaPages`.
 *
 * Every rule here is the Pages rule with the noun swapped, by owner decision
 * (2026-09-28): replace semantics EXACTLY like Pages, and no union or carry of
 * links the picker cannot show. Read each function beside its `metaPages.ts`
 * twin; a difference between the two is a defect unless its docblock says why.
 *
 * The same three-valued contract, evaluated in the same order:
 *   1. `linked_instagram_ids !== null` — otherwise the baseline is UNKNOWN,
 *   2. `unavailable_linked_instagram_ids` empty — otherwise it is UNREPRODUCIBLE,
 *   3. only then may an empty selection mean a deliberate clear.
 *
 * 🔴 `complete` is NOT part of that contract. It says whether every discovery
 * leg finished cleanly, and it drives exactly one thing: the neutral "may be
 * incomplete" notice. What makes an incomplete list safe is rule 2 — a linked
 * account missing from the list is unavailable, and an unavailable link forbids
 * the clear. Gating the clear on `complete` as well would add nothing but a
 * second, weaker reason to refuse.
 *
 * 🔴 Without the permission Meta answers "no permission" and "no accounts" with
 * the same `200 []`, so the step keys its call-to-action on `scopeGranted` and
 * never on an empty list. The server reports every linked account as unavailable
 * in that state, which is what already blocks the clear.
 */

import type {
  MetaInstagramAccount,
  MetaInstagramAccountsResponse,
  SaveMetaPayload,
  SelectedMetaPixel,
} from '../../api/client';
import {
  buildMetaSavePayload,
  selectionsDiffer,
  type MetaGrantReturn,
  type PagesOutcome,
  type PagesStepState,
} from './metaPages';

/**
 * May an EMPTY selection be read as a deliberate clear? — `canClearPages`.
 *
 * Neither `complete` nor `instagram_scope_granted` is consulted: without the
 * scope the server lists nothing and reports every linked id as unavailable, so a
 * non-empty linked set already blocks the clear, and a known-empty one is
 * vacuously reproducible.
 */
export function canClearInstagram(resp: MetaInstagramAccountsResponse): boolean {
  // An ABSENT field is not an empty one — the `canClearPages` reasoning: a
  // response that does not mention unavailable links cannot testify that there
  // are none. `Array.isArray` separates a real answer from `null` and `undefined`.
  if (!Array.isArray(resp.unavailable_linked_instagram_ids)) return false;
  if (!Array.isArray(resp.linked_instagram_ids)) return false;
  return resp.unavailable_linked_instagram_ids.length === 0;
}

/** Everything the Instagram step knows, in ONE atom — `PagesStepState`. */
export interface InstagramStepState {
  accounts: MetaInstagramAccount[];
  /** False → `accounts` is empty because no Graph call was made, not because the
   *  user has none. Offer the Instagram grant. */
  scopeGranted: boolean;
  /** Every discovery leg finished cleanly. Drives ONLY the "may be incomplete"
   *  notice — never `clearAllowed`, never Next. */
  complete: boolean;
  linkedIds: string[] | null;
  /** Linked accounts this picker cannot reproduce. Non-empty → no clear, and a
   *  replace unlinks them (`instagramDroppedByReplace`). */
  unavailableLinkedIds: string[];
  /** Precomputed so the gate is evaluated in exactly one place: the two
   *  `canClearPages` conditions, AND every linked account shown here
   *  (`instagramStepFromResponse`, the one intentional extra over Pages). */
  clearAllowed: boolean;
  /** What was linked (and offered) when this list was seeded. */
  baseline: string[];
  /** What is ticked now. */
  selected: string[];
}

/** The state before anything has loaded — and after a listing that FAILED.
 *
 *  `linkedIds: null` is the honest answer in both cases, and it is what makes a
 *  failed listing safe: we know nothing, so nothing on this step can clear.
 *  `scopeGranted` mirrors `EMPTY_PAGES_STEP`; the wizard renders this state only
 *  behind its loading / failed branches, so it is never read as a claim. */
export const EMPTY_INSTAGRAM_STEP: InstagramStepState = {
  accounts: [],
  scopeGranted: true,
  complete: false,
  linkedIds: null,
  unavailableLinkedIds: [],
  clearAllowed: false,
  baseline: [],
  selected: [],
};

/**
 * The ticks the user had on this step when they left for Meta's Instagram
 * dialog, and which rows were on screen then. Carried by the grant draft.
 */
export interface InstagramResumedSelection {
  selected: readonly string[];
  visibleIds: readonly string[];
}

/**
 * Build the step's state from a `/instagram-accounts` response —
 * `pagesStepFromResponse`, plus the grant round trip.
 *
 * 🔴 The baseline is established HERE, together with the seed it is compared
 * against, exactly as for Pages — there is no path that re-seeds the checkboxes
 * while keeping a stale baseline.
 *
 * `baseline` = linked ∩ offered. `selected` = the baseline, or — with `resumed`,
 * i.e. on the first listing after the Instagram grant — the user's own ticks
 * carried across the round trip (decision D7):
 *
 *     (resumed.selected ∩ offered) ∪ (linked ∩ offered ∖ resumed.visibleIds)
 *
 * so a tick survives, an untick of a row that was already on screen survives,
 * and a linked account the grant has only just made visible arrives ticked. This
 * is about not discarding what the user did; it has nothing to do with replace
 * semantics, which stay the Pages ones.
 */
export function instagramStepFromResponse(
  resp: MetaInstagramAccountsResponse,
  resumed?: InstagramResumedSelection | null,
): InstagramStepState {
  // Defensive in the `pagesStepFromResponse` sense: a malformed body degrades to
  // "baseline unknown" (no clear) instead of throwing inside the load promise.
  const scopeGranted = resp.instagram_scope_granted === true;
  // Without the scope nothing is listable, whatever the body carries: rows that
  // arrived anyway would be rows no grant stands behind, and a resumed tick
  // could otherwise land on one of them.
  const accounts = scopeGranted
    ? dedupeById(Array.isArray(resp.instagram_accounts) ? resp.instagram_accounts : [])
    : [];
  const linkedIds = Array.isArray(resp.linked_instagram_ids) ? resp.linked_instagram_ids : null;
  const linked = new Set(linkedIds ?? []);
  // In list order, so the seed is deterministic and matches what is on screen.
  const baseline = accounts.filter((a) => linked.has(a.id)).map((a) => a.id);

  // 🔴 The ONE intentional extra over `canClearPages` — defence in depth. The
  // server's `unavailable_linked_instagram_ids` is computed against ITS list; the
  // rows shown here are that list after the client's own filtering (the scope
  // gate above, `dedupeById`). A linked account the client dropped is not on
  // screen, so it must block the clear and be disclosed exactly like one the
  // server reported. With a correct server this set is already contained in the
  // server's, and nothing changes.
  const offered = new Set(accounts.map((a) => a.id));
  const unshownLinked = (linkedIds ?? []).filter((id) => !offered.has(id));
  const serverUnavailable = Array.isArray(resp.unavailable_linked_instagram_ids)
    ? resp.unavailable_linked_instagram_ids
    : [];
  const unavailableLinkedIds = [
    ...serverUnavailable,
    ...unshownLinked.filter((id) => !serverUnavailable.includes(id)),
  ];

  let selected = baseline;
  if (resumed) {
    const kept = new Set(resumed.selected);
    const wasVisible = new Set(resumed.visibleIds);
    selected = accounts
      .map((a) => a.id)
      .filter((id) => kept.has(id) || (linked.has(id) && !wasVisible.has(id)));
  }

  return {
    accounts,
    scopeGranted,
    complete: resp.complete === true,
    linkedIds,
    unavailableLinkedIds,
    // The server's rule (the `canClearPages` twin) AND "every linked account is
    // on screen here" — see the block above.
    clearAllowed: canClearInstagram(resp) && unshownLinked.length === 0,
    baseline,
    selected,
  };
}

/** The server dedupes by id; this keeps React's row keys unique if it ever does not. */
function dedupeById(accounts: readonly MetaInstagramAccount[]): MetaInstagramAccount[] {
  const seen = new Set<string>();
  const out: MetaInstagramAccount[] = [];
  for (const a of accounts) {
    if (!a || typeof a.id !== 'string' || seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

/**
 * May the user advance with the CURRENT selection applied? — `pagesNextEnabled`.
 *
 * Nothing changed → Skip is the honest exit. An empty selection without an
 * allowed clear → it would delete a set we never showed. 🔴 Never
 * `selected.length > 0`: that makes the deliberate clear unreachable.
 */
export function instagramNextEnabled(state: InstagramStepState): boolean {
  if (!selectionsDiffer(state.baseline, state.selected)) return false;
  if (state.selected.length === 0 && !state.clearAllowed) return false;
  return true;
}

/**
 * Linked Instagram accounts that pressing Next RIGHT NOW would unlink —
 * `pagesDroppedByReplace`.
 *
 * 🔴 The save is a replace and there is no carry rule (owner decision): the
 * server writes exactly the accounts its own discovery resolves from the
 * selection, so a linked account the picker cannot show is gone for good once
 * Next replaces the set. This is the only honest account of that one
 * destructive state. Gated on `instagramNextEnabled` for the Pages reason: when
 * Next is refused the save never leaves the browser, and warning about it would
 * be a second falsehood.
 */
export function instagramDroppedByReplace(state: InstagramStepState): string[] {
  if (!instagramNextEnabled(state)) return [];
  return state.unavailableLinkedIds.filter((id) => !state.selected.includes(id));
}

/** What advancing past the Instagram step decided — `PagesOutcome`. */
export type InstagramOutcome = { skipped: true } | { skipped: false; ids: string[] };

/**
 * The Instagram key of the save payload — or its ABSENCE. `metaPagesSavePatch`.
 *
 * 🔴 A skip sends NO key; `[]` is a deliberate clear. A `null` outcome means the
 * step was never left and is treated as a skip ("no statement" = "change
 * nothing"). Just the ids — no union with links the picker could not show.
 */
export function metaInstagramSavePatch(
  outcome: InstagramOutcome | null,
): { selected_instagram_account_ids?: string[] } {
  if (outcome === null || outcome.skipped) return {};
  return { selected_instagram_account_ids: outcome.ids };
}

/**
 * The whole save payload. Wraps `buildMetaSavePayload` — which is unchanged, so
 * the Page key keeps its one builder — and spreads the Instagram patch, so the
 * three-valued Instagram key is not reassembled in JSX either.
 */
export function buildMetaSavePayloadWithInstagram(args: {
  sessionId: string;
  adAccountIds: string[];
  pagesOutcome: PagesOutcome | null;
  instagramOutcome: InstagramOutcome | null;
  pixels: SelectedMetaPixel[];
}): SaveMetaPayload {
  return {
    ...buildMetaSavePayload({
      sessionId: args.sessionId,
      adAccountIds: args.adAccountIds,
      pagesOutcome: args.pagesOutcome,
      pixels: args.pixels,
    }),
    ...metaInstagramSavePatch(args.instagramOutcome),
  };
}

/** Toggle one account — `togglePage`. An id that is not on the list is inert:
 *  every offered account is selectable, and an unknown one is not an offered one. */
export function toggleInstagramAccount(
  state: InstagramStepState,
  accountId: string,
): InstagramStepState {
  if (!state.accounts.some((a) => a.id === accountId)) return state;
  const selected = state.selected.includes(accountId)
    ? state.selected.filter((id) => id !== accountId)
    : [...state.selected, accountId];
  return { ...state, selected };
}

/** Select-all / deselect-all over the ids currently shown — `toggleAllPages`. */
export function toggleAllInstagramAccounts(
  state: InstagramStepState,
  visibleIds: readonly string[],
): InstagramStepState {
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => state.selected.includes(id));
  const selected = allSelected
    ? state.selected.filter((id) => !visibleIds.includes(id))
    : Array.from(new Set([...state.selected, ...visibleIds]));
  return { ...state, selected };
}

// ---------------------------------------------------------------------------
// Resuming after the Instagram grant
// ---------------------------------------------------------------------------

/**
 * Put the Pages answer the user gave before the Instagram grant back on the Pages
 * step, so Back shows it — `pageIds ∩ listed`.
 *
 * Display only: the answer itself travels as the restored `pagesOutcome`, which
 * is what the save sends. Only an ANSWER is restored; a skip leaves the freshly
 * seeded selection alone, because the audit's Skip never touched it.
 */
export function restorePagesAnswer(
  state: PagesStepState,
  outcome: PagesOutcome | null,
): PagesStepState {
  if (outcome === null || outcome.skipped) return state;
  const listed = new Set(state.pages.map((p) => p.id));
  return { ...state, selected: outcome.pageIds.filter((id) => listed.has(id)) };
}

// ---------------------------------------------------------------------------
// Copy — beside the rules, for the `grantReturnNotice` reason: on this feature
// the WORDING has already been the defect once.
// ---------------------------------------------------------------------------

/** Equals the Instagram step title by owner decision on 2026-09-28, matching
 *  the mvp wizard. */
export const INSTAGRAM_GRANT_BUTTON_LABEL = 'Connect Instagram';

export const INSTAGRAM_LOADING_TEXT = 'Looking for Instagram accounts...';

/** Under the list. No promise about publishing: in this service the selection
 *  feeds the business profile, and nothing here publishes. */
export const INSTAGRAM_LIST_FOOTNOTES: readonly string[] = [
  'Selected accounts are saved with this Meta connection. We use them to profile your '
    + 'brand’s public presence.',
];

/** A title + body for the step states that show no rows, or `null` when rows are shown. */
export function instagramEmptyState(
  state: InstagramStepState,
): { title: string; body: string } | null {
  if (!state.scopeGranted) {
    return {
      title: 'Instagram accounts aren’t shared yet',
      body: 'Meta shares your businesses’ Instagram accounts only after you allow it. Connect '
        + 'Instagram to list them — you don’t need to select a Page for this. You can also '
        + 'skip this step and do it later.',
    };
  }
  if (state.accounts.length > 0) return null;
  if (state.complete) {
    return {
      title: 'No Instagram accounts found',
      body: 'None of the businesses you shared has an Instagram account we can list. If one '
        + 'should be here, connect Instagram again and share the business it belongs to.',
    };
  }
  return {
    title: 'Couldn’t list your Instagram accounts',
    body: 'Meta didn’t answer for every business in time. You can skip this step — nothing '
      + 'that’s connected will change — and try again later.',
  };
}

/** The only thing `complete` drives: shown over a non-empty but partial list. */
export function instagramIncompleteNotice(state: InstagramStepState): string | null {
  if (!state.scopeGranted || state.complete || state.accounts.length === 0) return null;
  return 'Meta didn’t answer for every business, so this list may be incomplete.';
}

/**
 * The Pages sentence for a linked set we cannot confirm — for the case the drop
 * panel does not already cover, i.e. an UNKNOWN baseline (`linkedIds === null`).
 *
 * The condition is `!clearAllowed`, the Pages presentability rule, so a
 * known-empty linked set (vacuously presentable) never shows it. When linked
 * accounts are merely unavailable the drop panel speaks instead: it says the
 * same thing and names the ids, and two statements of one fact read as two facts.
 */
export function instagramUnconfirmedNotice(state: InstagramStepState): string | null {
  if (!state.scopeGranted || state.clearAllowed || state.unavailableLinkedIds.length > 0) {
    return null;
  }
  return 'We couldn’t confirm every Instagram account that’s currently connected, so nothing '
    + 'will be removed unless you select accounts here.';
}

/** On the call-to-action, the `renderGrantPages` line: without the permission
 *  nothing can be selected, so existing links are left exactly as they are. */
export function instagramLinkedLeftAsIsText(linkedIds: readonly string[] | null): string | null {
  if (linkedIds === null || linkedIds.length === 0) return null;
  const n = linkedIds.length;
  return `${n} Instagram account${n !== 1 ? 's are' : ' is'} already linked and will be left `
    + `exactly as ${n !== 1 ? 'they are' : 'it is'}.`;
}

/**
 * The no-permission card's body after a trip through the Instagram dialog —
 * the twin of `grantPromptText`, or `null` for the first-visit body
 * (`instagramEmptyState(...).body`).
 *
 * 🔴 The first-visit text ("Meta shares … only after you allow it") says nothing
 * about an attempt, and repeated to someone who has just been through the dialog
 * and come back without the permission it reads as the app not having noticed —
 * so they retry the same dialog believing it missed their answer. The Instagram
 * redirect carries no grant outcome of its own; what we know is that this mount
 * resumed from the Instagram draft (`resumedFromGrant`) and that the listing
 * still reports no scope (the only place this is rendered). `ads_scope_revoked`
 * is the one outcome the redirect does report: the whole grant was discarded.
 */
export function instagramGrantPromptText(args: {
  resumedFromGrant: boolean;
  grantReturn: MetaGrantReturn;
}): string | null {
  if (!args.resumedFromGrant) return null;
  if (args.grantReturn.kind === 'ads_scope_revoked') {
    return 'That attempt was discarded because the ads permission was switched off, so we '
      + 'still can’t list your Instagram accounts. Try again with every permission ticked, or skip.';
  }
  return 'Facebook did not grant the Instagram permission, so we still can’t list your '
    + 'Instagram accounts. You can try again, or skip — your ad accounts are connected either way.';
}

/**
 * The Instagram-phrased `ads_scope_revoked` notice — `grantReturnNotice` for a
 * return from the Instagram grant. The Page sentence would tell the user to "link
 * a Page" when what they tried was to share Instagram accounts.
 */
export function instagramGrantReturnNotice(grantReturn: MetaGrantReturn): string | null {
  if (grantReturn.kind !== 'ads_scope_revoked') return null;
  return (
    'The ads permission was switched off on Facebook’s consent screen, so we kept your '
    + 'existing connection and applied nothing. Your ad accounts are untouched. To list your '
    + 'Instagram accounts, try again and leave every permission ticked — or skip the step.'
  );
}

export function instagramSelectionCount(n: number): string {
  return `${n} Instagram account${n !== 1 ? 's' : ''} selected`;
}

/**
 * The footer hint: why Next is inert, or — while Next would unlink accounts we
 * cannot show — that it would. `''` only when Next is enabled and harmless.
 * Rendered into an `aria-live` region that stays mounted for the whole step, so
 * it is the TEXT that changes — a live region added already holding its message
 * is widely not announced.
 *
 * 🔴 The destructive state must not be the silent one. The drop panel that
 * announces it is not a live region, so without this line the step's only live
 * region would go EMPTY at exactly the moment Next starts destroying a link.
 */
export function instagramFooterHint(state: InstagramStepState): string {
  const dropped = instagramDroppedByReplace(state);
  if (dropped.length > 0) {
    const n = dropped.length;
    return `Next will unlink ${n} Instagram account${n !== 1 ? 's' : ''} we can’t show here — `
      + 'use Skip to leave every link as it is.';
  }
  if (instagramNextEnabled(state)) return '';
  if (!selectionsDiffer(state.baseline, state.selected)) {
    return 'Nothing changed yet — use Skip to continue without touching your Instagram accounts.';
  }
  return 'Unticking everything can’t be saved here — use Skip to continue without touching '
    + 'your Instagram accounts.';
}

/** Meta may answer without a username; never render an empty row. */
export function instagramAccountTitle(account: MetaInstagramAccount): string {
  return account.username ? `@${account.username}` : account.id;
}

/** `name · via {business}` / `via Page {page}` / the id. */
export function instagramAccountSubtitle(account: MetaInstagramAccount): string | null {
  const via = account.business_name
    ? `via ${account.business_name}`
    : account.linked_page_name
      ? `via Page ${account.linked_page_name}`
      : null;
  const parts = [account.name, via].filter((p): p is string => typeof p === 'string' && p.trim() !== '');
  if (parts.length > 0) return parts.join(' · ');
  // The title already IS the id when there is no username; do not print it twice.
  return account.username ? account.id : null;
}

// ---------------------------------------------------------------------------
// The Instagram grant round trip (decision D12)
// ---------------------------------------------------------------------------

/**
 * A SEPARATE draft from the Page one (`pending_meta_page_grant`, unchanged). The
 * Instagram grant is a full server-side redirect through Meta, so the wizard
 * unmounts; this parks everything the user decided up to the Instagram step.
 */
export const META_INSTAGRAM_GRANT_DRAFT_KEY = 'pending_meta_instagram_grant';

export interface MetaInstagramGrantDraft {
  /** The grant comes back on the SAME id; a draft under another one belongs to a
   *  different connect attempt and must never be applied. */
  sessionId: string;
  adAccountIds: string[];
  /** How the Pages step was left — sent by the save exactly as restored. */
  pagesOutcome: PagesOutcome | null;
  instagramSelected: string[];
  /** Every row that was on the list: D7 needs "was it on screen", and the step
   *  has no search, so every row was. */
  instagramVisibleIds: string[];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** `metaPages.ts`'s `browserStore`, which that module does not export. */
function browserStore(): StorageLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Storage can throw outright (blocked cookies, private mode).
    return null;
  }
}

export function writeInstagramGrantDraft(
  draft: MetaInstagramGrantDraft,
  store: StorageLike | null = browserStore(),
): void {
  try {
    store?.setItem(META_INSTAGRAM_GRANT_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Losing the draft costs the user a re-pick, not the connection.
  }
}

/** Strictly an array of strings, or `null`. */
function stringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.every((v) => typeof v === 'string') ? (value as string[]) : null;
}

/**
 * A `PagesOutcome` read back from storage, or `undefined` when it is malformed.
 *
 * 🔴 No filtering here, unlike the ad-account ids: `{ skipped: false, pageIds:
 * [123] }` filtered down to `[]` would turn an unreadable answer into a
 * deliberate CLEAR of the user's Pages. A malformed outcome discards the draft.
 */
function parsePagesOutcome(value: unknown): PagesOutcome | null | undefined {
  if (value === null) return null;
  if (!value || typeof value !== 'object') return undefined;
  const outcome = value as { skipped?: unknown; pageIds?: unknown };
  if (outcome.skipped === true) return { skipped: true };
  if (outcome.skipped !== false) return undefined;
  const pageIds = stringArray(outcome.pageIds);
  return pageIds === null ? undefined : { skipped: false, pageIds };
}

export function readInstagramGrantDraft(
  sessionId: string,
  store: StorageLike | null = browserStore(),
): MetaInstagramGrantDraft | null {
  try {
    const raw = store?.getItem(META_INSTAGRAM_GRANT_DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const draft = parsed as Record<string, unknown>;
    if (draft.sessionId !== sessionId) return null;
    if (!Array.isArray(draft.adAccountIds)) return null;
    // Filtered, then checked — the `readPageGrantDraft` order and reason: a
    // wizard resumed past step 1 with no ad account is not a resumable wizard.
    const adAccountIds = draft.adAccountIds.filter((id): id is string => typeof id === 'string');
    if (adAccountIds.length === 0) return null;
    // `undefined` (absent or malformed) is not `null` (the step was never left).
    const pagesOutcome = parsePagesOutcome(draft.pagesOutcome);
    if (pagesOutcome === undefined) return null;
    const instagramSelected = stringArray(draft.instagramSelected);
    const instagramVisibleIds = stringArray(draft.instagramVisibleIds);
    if (instagramSelected === null || instagramVisibleIds === null) return null;
    return { sessionId, adAccountIds, pagesOutcome, instagramSelected, instagramVisibleIds };
  } catch {
    return null;
  }
}

export function clearInstagramGrantDraft(store: StorageLike | null = browserStore()): void {
  try {
    store?.removeItem(META_INSTAGRAM_GRANT_DRAFT_KEY);
  } catch {
    // ignore
  }
}
